import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMyDailyDirectClient } from "@mydailydirect/app-kit";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  PageHeader,
  InteractiveLoader,
  formatPaise,
  SearchBar,
  FilterBar,
  useToast,
} from "@mydailydirect/ui";
import { useNavigate } from "react-router-dom";
import { orderStatusMeta } from "../lib/orderStatus";
import { Package, ArrowRight, RotateCcw, Calendar, ShoppingBag } from "lucide-react";
import { useCart } from "../lib/cart";

export function OrdersPage() {
  const client = useMyDailyDirectClient();
  const navigate = useNavigate();
  const cart = useCart();
  const { toast } = useToast();

  const [search, setSearch] = useState("");
  const [activeStatusFilter, setActiveStatusFilter] = useState("ALL");

  const ordersQuery = useQuery({ queryKey: ["orders"], queryFn: () => client.orders.list() });

  const statusFilters = useMemo(() => {
    const orders = ordersQuery.data ?? [];
    return [
      { label: "All Orders", value: "ALL", count: orders.length },
      { label: "Placed", value: "PLACED", count: orders.filter((o) => o.status === "PLACED").length },
      { label: "Confirmed", value: "CONFIRMED", count: orders.filter((o) => o.status === "CONFIRMED").length },
      { label: "Lined", value: "LINED", count: orders.filter((o) => o.status === "LINED").length },
      {
        label: "Out for Delivery",
        value: "LOADED",
        count: orders.filter((o) => o.status === "LOADED" || o.status === "INVOICED").length,
      },
      {
        label: "Delivered",
        value: "DELIVERED",
        count: orders.filter((o) => o.status === "DELIVERED" || o.status === "SETTLED").length,
      },
      {
        label: "Exception",
        value: "EXCEPTION",
        count: orders.filter((o) => o.status === "EXCEPTION" || o.status === "RETURNED" || o.status === "RTO").length,
      },
    ];
  }, [ordersQuery.data]);

  const filteredOrders = useMemo(() => {
    let list = (ordersQuery.data ?? []).slice().sort((a, b) => b.id - a.id);

    if (activeStatusFilter !== "ALL") {
      list = list.filter((o) => {
        if (activeStatusFilter === "LOADED") return o.status === "LOADED" || o.status === "INVOICED";
        if (activeStatusFilter === "DELIVERED") return o.status === "DELIVERED" || o.status === "SETTLED";
        if (activeStatusFilter === "EXCEPTION") {
          return o.status === "EXCEPTION" || o.status === "RETURNED" || o.status === "RTO";
        }
        return o.status === activeStatusFilter;
      });
    }

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (o) =>
          o.id.toString().includes(q) ||
          (o.retailerId && o.retailerId.toString().includes(q))
      );
    }

    return list;
  }, [ordersQuery.data, activeStatusFilter, search]);

  const handleQuickReorder = (e: React.MouseEvent, order: any) => {
    e.stopPropagation();
    if (!order.lines || order.lines.length === 0) return;

    order.lines.forEach((line: any) => {
      const currentQty = cart.qtyOf(line.skuId);
      cart.setQty(line.skuId, currentQty + line.qty);
    });

    toast({
      title: "Reorder Items Added",
      description: `Added ${order.lines.length} items from Order #${order.id} to cart!`,
      variant: "success",
    });

    // Trigger cart drawer open event
    window.dispatchEvent(new CustomEvent("mdd:open-cart"));
  };

  return (
    <div className="space-y-5 pb-16 px-1 sm:px-0">
      <PageHeader
        title="Procurement Order History"
        description="Track and review all wholesale procurement orders, delivery schedules, and digital invoices."
        actions={
          <Button
            variant="primary"
            onClick={() => navigate("/")}
            className="font-bold bg-cta text-cta-foreground hover:brightness-105 active:scale-[0.99] shadow-sm rounded-control min-h-[44px]"
          >
            <ShoppingBag className="h-4 w-4 mr-2" /> Browse Catalog
          </Button>
        }
      />

      {/* Search Bar and Status Filter Bar */}
      <div className="space-y-3 bg-card border border-border rounded-card p-3 sm:p-4 shadow-2xs">
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search orders by Order # or Retailer ID..."
            className="flex-1 w-full"
          />
        </div>

        <FilterBar
          filters={statusFilters}
          activeFilter={activeStatusFilter}
          onChange={setActiveStatusFilter}
        />
      </div>

      {ordersQuery.isLoading ? (
        <div className="py-12">
          <InteractiveLoader
            title="Loading Your Wholesale Orders"
            subtitle="Fetching delivery schedule & khata slips..."
          />
        </div>
      ) : ordersQuery.isError ? (
        <ErrorState
          description="Failed to load your order history."
          action={{ label: "Retry", onClick: () => ordersQuery.refetch() }}
        />
      ) : filteredOrders.length === 0 ? (
        <EmptyState
          title={search || activeStatusFilter !== "ALL" ? "No matching orders" : "No orders placed yet"}
          description={
            search || activeStatusFilter !== "ALL"
              ? "Try adjusting your search query or status filter."
              : "Browse the wholesale catalog to place your first order."
          }
          action={
            search || activeStatusFilter !== "ALL"
              ? { label: "Clear Filters", onClick: () => { setSearch(""); setActiveStatusFilter("ALL"); } }
              : { label: "Browse Catalog", onClick: () => navigate("/") }
          }
        />
      ) : (
        <div className="grid gap-3 sm:gap-4">
          {filteredOrders.map((order) => {
            const meta = orderStatusMeta(order.status);
            const badgeVariant = meta.variant.replace("pulse", "").toLowerCase() as
              | "success"
              | "warning"
              | "default"
              | "destructive";

            return (
              <Card
                key={order.id}
                className="p-4 sm:p-5 cursor-pointer hover:border-border/90 hover:shadow-sm transition-all rounded-card group border-border bg-card"
                onClick={() => navigate(`/orders/${order.id}`)}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="space-y-2 flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                      <div className="flex items-center gap-2 font-black text-sm sm:text-base text-foreground group-hover:text-primary transition-colors">
                        <Package className="h-4 w-4 sm:h-5 sm:w-5 text-cta shrink-0" />
                        <span>Order #{order.id}</span>
                      </div>

                      <Badge dot variant={badgeVariant === "default" || !badgeVariant ? "default" : (badgeVariant as any)}>
                        {meta.label}
                      </Badge>

                      {(order as any).paymentMethod && (
                        <Badge
                          variant={order.status === "DELIVERED" || order.status === "SETTLED" ? "success" : "outline"}
                          className="font-extrabold text-2xs uppercase tracking-wide"
                        >
                          {order.status === "DELIVERED" || order.status === "SETTLED"
                            ? "Paid"
                            : (order as any).paymentMethod}
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center gap-3 sm:gap-4 text-xs text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5 shrink-0" />
                        <span>
                          {new Date(order.placedAt).toLocaleString("en-IN", {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })}
                        </span>
                      </span>
                      <span>·</span>
                      <span className="font-semibold text-foreground">
                        {(order.lines?.length ?? 0)} {(order.lines?.length ?? 0) === 1 ? "line item" : "line items"}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between md:justify-end gap-4 sm:gap-6 pt-3 md:pt-0 border-t md:border-t-0 border-border">
                    <div className="text-left md:text-right">
                      <span className="text-2xs uppercase font-extrabold text-muted-foreground block">
                        Total Amount
                      </span>
                      <span className="text-base sm:text-xl font-black text-foreground type-num">
                        {formatPaise(order.totalPaise)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => handleQuickReorder(e, order)}
                        className="h-10 min-h-[44px] sm:min-h-[36px] font-bold text-xs rounded-control border-border hover:bg-muted"
                      >
                        <RotateCcw className="h-3.5 w-3.5 mr-1.5 text-cta" /> Reorder
                      </Button>

                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate(`/orders/${order.id}`)}
                        className="h-10 min-h-[44px] sm:min-h-[36px] font-bold text-xs rounded-control bg-sunken hover:bg-muted text-foreground"
                      >
                        Details <ArrowRight className="h-3.5 w-3.5 ml-1" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
